import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Text,
  Platform,
} from 'react-native';
import { useVideoPlayer, VideoView } from 'expo-video';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useOptionalVideoContext } from '../contexts/VideoContext';
import { fontStyle as appFontStyle } from '../config/fonts';

interface VideoPlayerProps {
  uri: string;
  height?: number;
  width?: number;
  maxHeight?: number; // Maximum height for vertical videos in feed
  postId?: string;
  videoId?: string;
  isViewable?: boolean;
  isVisible?: boolean; // Alias for isViewable from PostListItem
  autoplay?: boolean;
  muted?: boolean;
  showControls?: boolean;
}

export default function VideoPlayer({
  uri,
  height,
  maxHeight,
  postId,
  videoId,
  isViewable,
  isVisible,
  autoplay,
  muted: initialMuted = true,
  showControls: showControlsProp = true,
}: VideoPlayerProps) {
  const { colors } = useTheme();
  // Using a unique ID for the global video context
  const id = useRef(videoId || postId || Math.random().toString()).current;

  // Support both isViewable and isVisible props (isVisible is alias from PostListItem)
  const shouldAutoplay = isVisible ?? isViewable ?? autoplay ?? true;

  const videoContext = useOptionalVideoContext();

  // Refs
  const videoRef = useRef<VideoView>(null);
  const webContainerRef = useRef<HTMLDivElement>(null);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(showControlsProp);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  // Controls visibility timer
  const controlsTimeoutRef = useRef<any>(null);

  // Initialize Video Player
  const player = useVideoPlayer(uri, (p) => {
    p.loop = true;
    p.muted = initialMuted; // Start muted based on prop
    p.timeUpdateEventInterval = 0.5; // Update every 500ms
  });

  // Track video aspect ratio
  useEffect(() => {
    // Initial check
    if (player.videoTrack?.size) {
      const { width, height } = player.videoTrack.size;
      if (width && height) {
        setAspectRatio(width / height);
      }
    }

    // Listener for track changes
    const subscription = player.addListener('videoTrackChange', (payload) => {
      if (payload.videoTrack?.size) {
        const { width, height } = payload.videoTrack.size;
        if (width && height) {
          setAspectRatio(width / height);
        }
      }
    });

    return () => {
      subscription.remove();
    };
  }, [player]);

  // Sync state with player
  useEffect(() => {
    // Correctly destructure event payload
    const subscription = player.addListener('playingChange', ({ isPlaying }: { isPlaying: boolean }) => {
      setIsPlaying(isPlaying);
      if (isPlaying) {
        setIsLoading(false);
        if (videoContext) {
          videoContext.setPlaying(id);
        }
      }
    });

    // Initial status check
    setIsPlaying(player.playing);
    setIsMuted(player.muted); // Sync initial mute state

    return () => {
      subscription.remove();
    };
  }, [player, id, videoContext]);

  useEffect(() => {
    const subscription = player.addListener('volumeChange', (payload) => {
      // VolumeChangeEventPayload has 'muted' not 'isMuted'
      setIsMuted((payload as any).muted ?? player.muted);
    });
    return () => subscription.remove();
  }, [player]);

  // Polling for duration and current time (more reliable than events for seek bar)
  useEffect(() => {
    const interval = setInterval(() => {
      if (player) {
        if (player.duration > 0 && duration !== player.duration) {
          setDuration(player.duration);
        }
        setCurrentTime(player.currentTime);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [player, duration]);

  // Handle auto-play/pause based on viewability
  // Use a ref to prevent race conditions between play() and pause() calls
  const playIntentRef = useRef(shouldAutoplay);
  playIntentRef.current = shouldAutoplay;

  useEffect(() => {
    if (shouldAutoplay) {
      // Small delay to batch rapid visibility changes and avoid play/pause races
      const timer = setTimeout(() => {
        if (playIntentRef.current) {
          player.play();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      player.pause();
    }
  }, [shouldAutoplay, player]);

  // Handle global context (only one video plays at a time)
  useEffect(() => {
    if (!videoContext) return;

    // If another video is playing, pause this one
    if (videoContext.currentlyPlayingId && videoContext.currentlyPlayingId !== id && isPlaying) {
      player.pause();
    }
  }, [videoContext?.currentlyPlayingId, id, isPlaying, player]);

  // Manage Controls Visibility
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying, resetControlsTimeout]);

  // Handlers
  const togglePlayPause = useCallback(() => {
    if (player.playing) {
      player.pause();
    } else {
      player.play();
      // Ensure we become the active video in context
      if (videoContext) {
        videoContext.setPlaying(id);
      }
    }
    resetControlsTimeout();
  }, [player, resetControlsTimeout, id, videoContext]);

  const toggleMute = useCallback(() => {
    player.muted = !player.muted;
    resetControlsTimeout();
  }, [player, resetControlsTimeout]);

  // Fullscreen handlers
  const handleFullscreenEnter = useCallback(() => {
    setIsFullscreen(true);
    // Auto-unmute when entering fullscreen on mobile
    if (Platform.OS !== 'web') {
      player.muted = false;
    }
  }, [player]);

  const handleFullscreenExit = useCallback(() => {
    setIsFullscreen(false);
    // Re-mute when exiting fullscreen on mobile
    if (Platform.OS !== 'web') {
      player.muted = true;
    }
  }, [player]);

  // Fullscreen logic - different for web vs native
  const openFullscreen = useCallback(() => {
    if (Platform.OS === 'web') {
      // On web, use the browser's native Fullscreen API on the video element
      const container = webContainerRef.current;
      if (container) {
        const videoElement = container.querySelector('video');
        if (videoElement) {
          if (videoElement.requestFullscreen) {
            videoElement.requestFullscreen();
          } else if ((videoElement as any).webkitRequestFullscreen) {
            (videoElement as any).webkitRequestFullscreen();
          } else if ((videoElement as any).msRequestFullscreen) {
            (videoElement as any).msRequestFullscreen();
          }
        }
      }
    } else {
      // On native, use expo-video's enterFullscreen
      if (videoRef.current) {
        videoRef.current.enterFullscreen();
      }
    }
  }, []);

  const handleVideoPress = useCallback(() => {
    // On mobile, tap opens fullscreen directly
    if (Platform.OS !== 'web') {
      openFullscreen();
      return;
    }
    // On web: first click unmutes, subsequent clicks toggle play/pause
    if (isMuted) {
      // First click - unmute the video
      player.muted = false;
      resetControlsTimeout();
    } else {
      // Already unmuted - toggle play/pause
      if (player.playing) {
        player.pause();
      } else {
        player.play();
        if (videoContext) {
          videoContext.setPlaying(id);
        }
      }
      resetControlsTimeout();
    }
  }, [isMuted, player, resetControlsTimeout, openFullscreen, videoContext, id]);

  const handleSeek = useCallback((percentage: number) => {
    if (duration > 0) {
      const seekTime = duration * percentage;
      player.currentTime = seekTime;
      setCurrentTime(seekTime); // Optimistic update
      resetControlsTimeout();
    }
  }, [duration, player, resetControlsTimeout]);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const fontStyle = {
    ...appFontStyle,
    fontSize: 12,
    color: '#fff',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  };

  // Web-only seek bar component
  const WebSeekBar = ({ progress, onSeek }: { progress: number; onSeek: (pct: number) => void }) => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
      onSeek(percentage);
    };

    return (
      <div
        style={{
          flex: 1,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        <div style={{
          height: 3,
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderRadius: 1.5,
          width: '100%',
          position: 'relative',
        }}>
          <div style={{
            height: '100%',
            backgroundColor: '#fff',
            borderRadius: 1.5,
            width: `${Math.max(0, Math.min(100, progress * 100))}%`,
          }} />
          <div style={{
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: '#fff',
            position: 'absolute',
            top: -3.5,
            left: `${Math.max(0, Math.min(100, progress * 100))}%`,
            marginLeft: -5,
          }} />
        </div>
      </div>
    );
  };

  // --- Controls Component ---
  const VideoControls = () => {
    if (!showControls) return null;

    const [seekBarWidth, setSeekBarWidth] = useState(0);
    const progress = duration > 0 ? currentTime / duration : 0;

    const seekToPosition = (offsetX: number) => {
      if (seekBarWidth > 0) {
        const percentage = Math.max(0, Math.min(1, offsetX / seekBarWidth));
        handleSeek(percentage);
      }
    };

    // Web click handler for seek bar
    const handleWebSeekClick = (e: any) => {
      // Using 'any' for React Native Web event compat
      if (Platform.OS === 'web') {
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        seekToPosition(offsetX);
      }
    };

    return (
      <View style={styles.controlsContainer} pointerEvents="box-none">
        {/* Time Display */}
        <Text style={[styles.durationText, fontStyle]}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>

        <View style={styles.controlsRow}>
          {/* Play/Pause Button */}
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            style={styles.playPauseButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name={isPlaying ? 'pause' : 'play'} size={28} color="#fff" />
          </Pressable>

          {/* Seek Bar */}
          <View
            style={styles.seekBarWrapper}
            onLayout={(e) => setSeekBarWidth(e.nativeEvent.layout.width)}
          >
            {Platform.OS === 'web' ? (
              // Web Seek Bar
              <div
                style={{
                  flex: 1,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={(e) => handleWebSeekClick(e)}
              >
                <View style={styles.seekBarTrack}>
                  <View style={[styles.seekBarProgress, { width: `${Math.max(0, Math.min(100, progress * 100))}%` }]} />
                  <View style={[styles.seekBarThumb, { left: `${Math.max(0, Math.min(100, progress * 100))}%` }]} />
                </View>
              </div>
            ) : (
              // Native Seek Bar
              <Pressable
                style={{ flex: 1, height: 40, justifyContent: 'center' }}
                onTouchStart={(e) => seekToPosition(e.nativeEvent.locationX)}
                onTouchMove={(e) => seekToPosition(e.nativeEvent.locationX)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <View style={styles.seekBarTrack}>
                  <View style={[styles.seekBarProgress, { width: `${Math.max(0, Math.min(100, progress * 100))}%` }]} />
                  <View style={[styles.seekBarThumb, { left: `${Math.max(0, Math.min(100, progress * 100))}%` }]} />
                </View>
              </Pressable>
            )}
          </View>
        </View>

        {/* Top Right Buttons (Mute, Fullscreen) */}
        {Platform.OS === 'web' ? (
          <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'row', zIndex: 10 }}>
            <div
              style={{
                padding: 8,
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderRadius: 20,
                marginLeft: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleMute();
              }}
            >
              <Ionicons name={isMuted ? 'volume-mute' : 'volume-high'} size={20} color="#fff" />
            </div>
            <div
              style={{
                padding: 8,
                backgroundColor: 'rgba(0,0,0,0.4)',
                borderRadius: 20,
                marginLeft: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openFullscreen();
              }}
            >
              <Feather name="maximize" size={18} color="#fff" />
            </div>
          </div>
        ) : (
          <View style={styles.topRightButtons}>
            <Pressable
              style={styles.iconButton}
              onPress={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
            >
              <Ionicons name={isMuted ? 'volume-mute' : 'volume-high'} size={20} color="#fff" />
            </Pressable>
            <Pressable
              style={styles.iconButton}
              onPress={(e) => {
                e.stopPropagation();
                openFullscreen();
              }}
            >
              <Feather name="maximize" size={18} color="#fff" />
            </Pressable>
          </View>
        )}
      </View>
    );
  };

  // Web specific hover handlers
  const handleWebMouseEnter = useCallback(() => {
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  const handleWebMouseLeave = useCallback(() => {
    if (isPlaying) setShowControls(false);
  }, [isPlaying]);

  // Calculate container style with optional maxHeight constraint
  // When maxHeight is provided, use it directly as the height for vertical videos
  // to prevent aspectRatio from shrinking the container width
  const useExplicitHeight = maxHeight && aspectRatio < 1; // Vertical video with maxHeight

  const containerStyle = [
    styles.container,
    height
      ? { height }
      : useExplicitHeight
        ? { height: maxHeight } // Use maxHeight directly, video will be centered with contentFit="contain"
        : { aspectRatio },
  ];

  return (
    <View style={containerStyle}>
      {Platform.OS === 'web' ? (
        // Web layout - use div wrapper for proper mouse events
        <div
          ref={webContainerRef}
          style={{ width: '100%', height: '100%', position: 'relative' }}
          onMouseEnter={handleWebMouseEnter}
          onMouseLeave={handleWebMouseLeave}
        >
          {/* Video layer */}
          <VideoView
            ref={videoRef}
            player={player}
            style={styles.video}
            contentFit="contain"
            nativeControls={false}
            allowsPictureInPicture
            onFullscreenEnter={handleFullscreenEnter}
            onFullscreenExit={handleFullscreenExit}
          />

          {/* Clickable overlay for web - sits above video but below controls */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleVideoPress();
            }}
          />

          {isLoading && (
            <View style={[styles.loadingOverlay, { zIndex: 2 }]}>
              <ActivityIndicator size="large" color="#fff" />
            </View>
          )}

          {/* Web Controls - rendered directly with proper z-index */}
          {showControls && (
            <>
              {/* Time and seek bar */}
              <div style={{ position: 'absolute', bottom: 10, left: 12, right: 12, zIndex: 10 }}>
                <Text style={[styles.webDurationText, fontStyle]}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <div
                    style={{ padding: 8, marginRight: 8, cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
                  >
                    <Ionicons name={isPlaying ? 'pause' : 'play'} size={28} color="#fff" />
                  </div>
                  <WebSeekBar
                    progress={duration > 0 ? currentTime / duration : 0}
                    onSeek={handleSeek}
                  />
                </div>
              </div>

              {/* Top right buttons */}
              <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'row', zIndex: 10 }}>
                <div
                  style={{
                    padding: 8,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    borderRadius: 20,
                    marginLeft: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleMute(); }}
                >
                  <Ionicons name={isMuted ? 'volume-mute' : 'volume-high'} size={20} color="#fff" />
                </div>
                <div
                  style={{
                    padding: 8,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    borderRadius: 20,
                    marginLeft: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); openFullscreen(); }}
                >
                  <Feather name="maximize" size={18} color="#fff" />
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        // Native layout
        <Pressable
          style={styles.videoWrapper}
          onPress={handleVideoPress}
        >
          <VideoView
            ref={videoRef}
            player={player}
            style={styles.video}
            contentFit="contain"
            nativeControls={isFullscreen} // Show native controls in fullscreen on mobile for dismiss UI
            allowsPictureInPicture
            onFullscreenEnter={handleFullscreenEnter}
            onFullscreenExit={handleFullscreenExit}
          />

          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#fff" />
            </View>
          )}

          <VideoControls />
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#000',
    overflow: 'hidden',
    alignSelf: 'center',
  },
  videoWrapper: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  video: {
    width: '100%',
    height: '100%',
  },
  controlsWrapper: {
    ...StyleSheet.absoluteFillObject,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 1,
  },
  controlsContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    paddingBottom: 10,
    paddingHorizontal: 12,
    zIndex: 2,
  },
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  playPauseButton: {
    padding: 8,
    marginRight: 8,
  },
  durationText: {
    position: 'absolute',
    bottom: 50,
    left: 12,
    marginBottom: 4,
  },
  webDurationText: {
    marginBottom: 4,
  },
  seekBarWrapper: {
    flex: 1,
    height: 40,
    justifyContent: 'center',
  },
  seekBarTrack: {
    height: 3,
    backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 1.5,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  seekBarProgress: {
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 1.5,
  },
  seekBarThumb: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
    position: 'absolute',
    marginLeft: -5,
  },
  topRightButtons: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
  },
  iconButton: {
    padding: 8,
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 20,
    marginLeft: 8,
  }
});
